import { FormEvent, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SlotPicker from "../SlotPicker";
import BookingConfirmation from "../BookingConfirmation";
import ExistingMeeting from "../ExistingMeeting";
import GuestFields from "../GuestFields";
import { cleanGuests } from "../../utils/guests";
import { useLandingContext } from "../../contexts/LandingContext";
import {
  CONTACT_FIELDS,
  type ContactField,
  emptyContact,
  primaryButton,
  secondaryButton,
  SERVER_FIELDS,
  validateContact,
} from "../../utils/bookingContact";
import {
  type Availability,
  type Booking,
  type ExistingBooking,
  type Guest,
  browserTimezone,
  newIdempotencyKey,
  SchedulerError,
  schedulerApi,
} from "../../services/schedulerApi";
import { type Answers, type CartItem, CatalogError, catalogApi, type Picks, type Turn } from "../../services/catalogApi";

/**
 * Checkout: contact details, then the active-project message, the visitor's existing meeting, or the time picker,
 * guests and confirmation. Prices and plans are covered on the call, never here.
 */
const CatalogCheckout: React.FC<{
  picks: Picks;
  answers: Answers;
  cart: CartItem[];
  transcript: Turn[];
  summary: string;
  onBack: () => void;
  onBooked: () => void;
}> = ({ picks, answers, cart, transcript, summary, onBack, onBooked }) => {
  const { utmId, utmSource, referredBy } = useLandingContext();
  const [form, setForm] = useState(emptyContact);
  const [errors, setErrors] = useState<Partial<Record<ContactField, string>>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [activeProject, setActiveProject] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [intakeId, setIntakeId] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [existing, setExisting] = useState<ExistingBooking | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [guestsInvalid, setGuestsInvalid] = useState(false);
  const bookingKey = useRef<{ slot: string; key: string } | null>(null);

  const onSubmitDetails = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const found = validateContact(form);
    setErrors(found);
    setNotice(null);
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    try {
      const result = await catalogApi.intake({
        first_name: form.firstName.trim(), last_name: form.lastName.trim(), job_title: form.jobTitle.trim(),
        company: form.company.trim(), email: form.email.trim().toLowerCase(), timezone: browserTimezone(),
        utm_source: utmSource, utm_id: utmId, ...(referredBy ? { referred_by: referredBy } : {}),
        picks, answers, cart, transcript, assessment_summary: summary,
      });
      if ("existing_booking" in result) {
        setExisting(result.existing_booking);
        return;
      }
      setIntakeId(result.intake_id);
      setAvailability(await schedulerApi.availability({ intake: result.intake_id }));
      setSelected(null);
    } catch (error) {
      if (error instanceof CatalogError && error.code === "SOW_ACTIVE") {
        setActiveProject(error.message);
        return;
      }
      if (error instanceof CatalogError && error.fields.length > 0) {
        setErrors(Object.fromEntries(error.fields.filter((f) => SERVER_FIELDS[f])
          .map((f) => [SERVER_FIELDS[f], "Please check this field."])));
      }
      setNotice(error instanceof CatalogError || error instanceof SchedulerError ? error.message
        : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async () => {
    if (!intakeId || !selected) return;
    const invited = cleanGuests(guests);
    setGuestsInvalid(invited === null);
    if (invited === null) return;
    const attempt = `${selected}|${JSON.stringify(invited)}`;
    if (bookingKey.current?.slot !== attempt) bookingKey.current = { slot: attempt, key: newIdempotencyKey() };
    setBusy(true);
    setNotice(null);
    try {
      const result = await schedulerApi.book(
        { intake_id: intakeId, start: selected, ...(invited.length ? { guests: invited } : {}) }, bookingKey.current.key);
      setBooking(result.booking);
      onBooked();
    } catch (error) {
      if (error instanceof SchedulerError && error.code === "ALREADY_BOOKED" && error.existing) {
        setExisting(error.existing);
        return;
      }
      if (error instanceof SchedulerError && error.code === "SLOT_TAKEN" && error.slots && availability) {
        setAvailability({ ...availability, slots: error.slots });
        setSelected(null);
      } else if (error instanceof SchedulerError && ["INTAKE_EXPIRED", "INTAKE_NOT_FOUND"].includes(error.code)) {
        setIntakeId(null);
        setAvailability(null);
      }
      setNotice(error instanceof SchedulerError ? error.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const step = booking ? "done" : activeProject ? "active" : existing ? "existing" : availability ? "slots" : "details";

  return (
    <div className="rounded-2xl border border-borderSubtle bg-surface p-6 shadow-xl sm:p-8">
      <h2 className="text-2xl font-bold text-text-primary">
        {step === "done" ? "You're booked" : "Book a call to go over your plan"}
      </h2>
      <p className="mt-2 text-text-secondary">
        {step === "details" && "We'll go over your selections, timing and pricing, and confirm the right plan for you."}
        {step === "slots" && availability && `Choose a ${availability.meeting_minutes}-minute time with ${availability.lead.name}.`}
        {step === "existing" && "You already have a meeting with us."}
        {step === "active" && "We're already working together."}
        {step === "done" && "We look forward to talking with you."}
      </p>
      {notice && <p role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{notice}</p>}

      {step === "details" && (
        <form noValidate onSubmit={onSubmitDetails} className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
          {CONTACT_FIELDS.map((field) => (
            <div key={field.name} className={field.name === "email" ? "md:col-span-2" : undefined}>
              <label htmlFor={`catalog-${field.name}`} className="mb-2 block text-sm font-medium text-text-primary">
                {field.label} *
              </label>
              <input id={`catalog-${field.name}`} name={field.name} type="text" inputMode={field.inputMode}
                     autoComplete={field.autoComplete} value={form[field.name]}
                     aria-invalid={Boolean(errors[field.name])}
                     aria-describedby={errors[field.name] ? `catalog-${field.name}-error` : undefined}
                     onChange={(event) => setForm((f) => ({ ...f, [field.name]: event.target.value }))}
                     className={`w-full rounded-lg border bg-white px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                       errors[field.name] ? "border-red-500" : "border-borderSubtle"}`} />
              {errors[field.name] && (
                <p id={`catalog-${field.name}-error`} className="mt-1 text-xs text-red-500">{errors[field.name]}</p>
              )}
            </div>
          ))}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between md:col-span-2">
            <button type="button" onClick={onBack} disabled={busy} className={secondaryButton}>
              <ArrowLeft className="mr-2 h-5 w-5" /> Back to the catalog
            </button>
            <button type="submit" disabled={busy} className={`${primaryButton} group`}>
              {busy ? "Finding times..." : "See available times"}
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </form>
      )}

      {step === "slots" && availability && (
        <div className="mt-6">
          <SlotPicker slots={availability.slots} timeZone={availability.timezone} selected={selected}
                      onSelect={setSelected} disabled={busy} />
          <GuestFields guests={guests} onChange={setGuests} disabled={busy} invalid={guestsInvalid} />
          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            <button type="button" onClick={() => { setAvailability(null); setIntakeId(null); }} disabled={busy}
                    className={secondaryButton}>
              <ArrowLeft className="mr-2 h-5 w-5" /> Edit details
            </button>
            <button type="button" onClick={() => void onConfirm()} disabled={busy || !selected} className={primaryButton}>
              {busy ? "Booking..." : "Confirm booking"}
            </button>
          </div>
        </div>
      )}

      {step === "active" && activeProject && (
        <div className="mt-6 space-y-4">
          <p className="rounded-xl bg-surfaceSoft px-4 py-3 text-text-primary">{activeProject}</p>
          <button type="button" onClick={onBack} className={secondaryButton}>
            <ArrowLeft className="mr-2 h-5 w-5" /> Back to the catalog
          </button>
        </div>
      )}

      {step === "existing" && existing && (
        <div className="mt-6">
          <ExistingMeeting existing={existing} email={form.email.trim().toLowerCase()} company={form.company.trim()} />
        </div>
      )}

      {step === "done" && booking && <div className="mt-6"><BookingConfirmation booking={booking} /></div>}
    </div>
  );
};

export default CatalogCheckout;
